import { useEffect, useRef, type ReactNode } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useMap } from 'react-map-gl';

/**
 * The map's extension point for its control stack — the column beside the map holding
 * Layers, Pricing, Measure and the rest.
 *
 * A feature that wants a button there registers one from inside the map, and the stack
 * draws whatever is registered without knowing what any of it is. That keeps the
 * dependency pointing the right way: the map defines the contract, features depend on
 * the map, and the map depends on none of them. Adding a second feature to the stack
 * needs no change here.
 */

export interface MapControl {
  /**
   * Stable identity. A remount replaces its own entry rather than adding a second, so
   * React strict mode and map swaps cannot leave a stale button behind.
   */
  id: string;
  /** Position in the stack; lower sits higher up. Ties keep registration order. */
  order?: number;
  /**
   * Draws the control, given the stack's own button class so a contributed control
   * matches the native ones, and a way to shut the layers panel — which opens over
   * the same corner and would otherwise cover whatever the control puts on screen.
   */
  render: (props: { className: string; closePanels: () => void }) => ReactNode;
}

interface Registered extends MapControl {
  /**
   * The map it was registered from, so it shows on that map alone and another map's
   * unmount can't take it away. Unset when registered outside any map.
   */
  map?: unknown;
}

const registeredAtom = atom<Registered[]>([]);

/** What the control stacks draw between them, in the order they draw them. */
export const mapControlsAtom = atom((get) =>
  [...get(registeredAtom)].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
);

type Registration =
  | { type: 'add'; control: Registered }
  | { type: 'remove'; id: string; map?: unknown };

const registerAtom = atom(null, (get, set, action: Registration) => {
  const { id, map } = action.type === 'add' ? action.control : action;
  const without = get(registeredAtom).filter(
    (control) => control.id !== id || control.map !== map
  );
  set(
    registeredAtom,
    action.type === 'add' ? [...without, action.control] : without
  );
});

/**
 * Puts a control in the map's stack for as long as the caller is mounted.
 *
 * Call it from a component rendered inside `<Map>`. `render` is held by reference and
 * read at draw time, so a caller need not memoise it — only `id` and `order` decide
 * when the registration itself changes.
 *
 * @param {MapControl} control - The control to contribute.
 */
export const useMapControl = ({ id, order, render }: MapControl): void => {
  const renderRef = useRef(render);
  renderRef.current = render;
  const register = useSetAtom(registerAtom);
  const map = useMap().current?.getMap();

  useEffect(() => {
    register({
      type: 'add',
      control: {
        id,
        order,
        map,
        render: (props) => renderRef.current(props),
      },
    });
    return () => register({ type: 'remove', id, map });
  }, [id, order, map, register]);
};

/**
 * The controls this map's stack draws: its own, and any registered outside a map.
 * Call it from inside `<Map>`.
 *
 * @return {MapControl[]} The controls, in stack order.
 */
export const useOwnMapControls = (): MapControl[] => {
  const controls = useAtomValue(mapControlsAtom);
  const map = useMap().current?.getMap();
  return controls.filter((control) => !control.map || control.map === map);
};
